import React, { useMemo } from "react";

import searchTerms, { termsRegex } from "./searchTerms";

/**
 * Wraps the searched terms in `<mark>` wherever they appear in a string.
 *
 * WHY IT RENDERS NODES AND NEVER HTML
 *
 * The text here is a dealer's WhatsApp message — third-party content this app
 * does not control. Building a string of `<mark>` tags and handing it to
 * `dangerouslySetInnerHTML` would execute whatever a dealer put in a message,
 * which is a stored-XSS hole on a page only master admins can open but every
 * dealer can write to. So this splits the string and returns React nodes; React
 * escapes each text segment for us and there is no HTML string at any point.
 *
 * MATCHING
 *
 * Every whitespace-separated term is highlighted independently, so searching
 * "126500 rolex" marks both words rather than only the exact phrase. The term
 * rules — escaping, longest-first, single characters dropped — live in
 * searchTerms.js because matchSnippet has to reach the same answer.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 *
 * It does not try to reproduce what the server matched. Atlas Search is fuzzy
 * and matches per token with typo tolerance, so a card can legitimately be a
 * result without containing the literal query. In that case nothing is marked
 * and the card still renders — an honest "no visible match" beats inventing a
 * highlight that misrepresents why the row came back.
 */
const HighlightMatch = ({ text, query }) => {
  const parts = useMemo(() => {
    const value = typeof text === "string" ? text : "";
    if (value === "") return [value];

    // Shared with matchSnippet, which picks WHICH line of a long message the
    // card shows because it contains one of these. If the two disagreed, that
    // line would arrive here with nothing to mark on it.
    const re = termsRegex(searchTerms(query), "gi");
    if (!re) return [value];

    return value.split(re);
  }, [text, query]);

  if (parts.length === 1) return parts[0];

  // String.split with ONE capture group yields alternating segments: even
  // indexes are the text between matches, odd indexes are the matches
  // themselves. That is what makes this safe without comparing strings.
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <mark className="tf-hit" key={`${i}-${part}`}>
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </>
  );
};

export default HighlightMatch;
